import type { PolicyViewModel } from 'src/api/types/PolicyViewModel';
import type { CodeDescriptionLookupOption } from 'src/api/services/policy.service';

import dayjs from 'dayjs';
import { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers';
import { Box, MenuItem, TextField, Typography } from '@mui/material';

import { PolicyReadOnlyFields } from './policy-information-read-only-fields';
import {
  toSelectOptions,
  formFieldWrapperSx,
  type PolicyDraftSetter,
  buildSelectOptionsWithLegacyValue,
} from './policy-information-edit.helpers';

import type { PolicyEditDraft, PolicyFieldErrors } from './policy-information.types';

type PolicyInformationPolicyFieldsProps = {
  viewPolicy: PolicyViewModel;
  draft: PolicyEditDraft;
  setDraft: PolicyDraftSetter;
  policyTypeLookups: CodeDescriptionLookupOption[];
  fieldErrors: PolicyFieldErrors;
};

export function PolicyInformationPolicyFields({
  viewPolicy,
  draft,
  setDraft,
  policyTypeLookups,
  fieldErrors,
}: Readonly<PolicyInformationPolicyFieldsProps>) {
  const policyTypeOptions = buildSelectOptionsWithLegacyValue(
    draft.policyTypeCode,
    toSelectOptions(policyTypeLookups),
    viewPolicy.policyTypeEnumDescription
  );

  return (
    <Box sx={{ padding: '1.5rem' }}>
        <Typography
          variant="h5"
          sx={{ color: 'text.primary' }}
          data-testid="coverage-profile-title"
        >
          Policy
        </Typography>
        <Grid container spacing={2} columns={16}>
          <PolicyReadOnlyFields viewPolicy={viewPolicy} />
          <Grid size={{ xs: 8, md: 4 }}>
            <Box sx={formFieldWrapperSx}>
              <TextField
                fullWidth
                size="small"
                label="Agency"
                value={draft.producerName}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, producerName: event.target.value }))
                }
                slotProps={{ htmlInput: { maxLength: 30 } }}
                error={Boolean(fieldErrors.producerName)}
                helperText={fieldErrors.producerName}
                data-testid="agency-input"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <Box sx={formFieldWrapperSx}>
              <TextField
                select
                fullWidth
                size="small"
                label="Policy type"
                value={draft.policyTypeCode}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, policyTypeCode: event.target.value }))
                }
                error={Boolean(fieldErrors.policyTypeCode)}
                helperText={fieldErrors.policyTypeCode}
                data-testid="policy-type-select"
              >
                {policyTypeOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={!policyTypeLookups.some((lookup) => lookup.code === option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <DateField
              testId="effective-date-input"
              label="Effective date"
              value={draft.policyEffectiveDate}
              error={fieldErrors.policyEffectiveDate}
              setDraft={setDraft}
              field="policyEffectiveDate"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <DateField
              testId="expiration-date-input"
              label="Expiration date"
              value={draft.policyExpirationDate}
              error={fieldErrors.policyExpirationDate}
              setDraft={setDraft}
              field="policyExpirationDate"
            />
          </Grid>
          <Grid size={{ xs: 8, md: 4 }}>
            <DateField
              testId="issue-date-input"
              label="Issue date"
              value={draft.policyIssueDate}
              error={fieldErrors.policyIssueDate}
              setDraft={setDraft}
              field="policyIssueDate"
            />
          </Grid>
        </Grid>
      </Box>
  );
}

type DateFieldProps = {
  testId: string;
  label: string;
  field: 'policyEffectiveDate' | 'policyExpirationDate' | 'policyIssueDate';
  value: string;
  error?: string;
  setDraft: PolicyDraftSetter;
};

function DateField({ testId, label, field, value, error, setDraft }: Readonly<DateFieldProps>) {
  const [pickerValue, setPickerValue] = useState(() => (value ? dayjs(value, 'MM/DD/YYYY') : null));
  const [hasInputError, setHasInputError] = useState(false);

  useEffect(() => {
    if (!hasInputError) {
      setPickerValue(value ? dayjs(value, 'MM/DD/YYYY') : null);
    }
  }, [hasInputError, value]);

  return (
    <Box sx={formFieldWrapperSx} data-testid={testId}>
      <DatePicker
        label={label}
        format="MM/DD/YYYY"
        value={pickerValue}
        onChange={(nextValue) => {
          setPickerValue(nextValue);
          if (nextValue === null) {
            setHasInputError(false);
            setDraft((current) => ({ ...current, [field]: '' }));
          } else if (nextValue.isValid()) {
            setHasInputError(false);
            setDraft((current) => ({ ...current, [field]: nextValue.format('MM/DD/YYYY') }));
          }
        }}
        onError={(reason) => {
          setHasInputError(Boolean(reason));
          if (reason) setDraft((current) => ({ ...current, [field]: '' }));
        }}
        slotProps={{
          textField: { fullWidth: true, size: 'small', error: Boolean(error), helperText: error },
        }}
      />
    </Box>
  );
}
